import styled from '@emotion/styled';
import { useCallback } from 'react';
import { tokens, Text, Loader, Icon, IconButton } from '@hotjar/hotsauce';
import { Line } from '@hotjar/charts';
import { trackClick } from '@hotjar/tracking';
import { useTranslation } from 'react-i18next';

import type { ButtonKind, IconName } from '@hotjar/hotsauce';
import type { GlobalFilter } from '@hotjar/global-filters';
import type { TrackableSite } from '../../../../constants-types/ExternalDependencyTypes';

import { generateNavigationLinkFromHeatmaps } from '../../../../utils/generateNavigationLinkFromHeatmaps';
import { getTrackGroupDataAndTriggeredOnState } from '../../../../utils/trackingUtils';
import { EVENT_NAMES } from '../../../../constants-types/TrackEventNames';
import { EVENT_UI_POSITIONS } from '../../../../constants-types/TrackEventUIPositions';

const CONTENT_HEIGHT = 40;

const Content = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
  justify-content: flex-start;
  align-items: center;
  padding-top: ${tokens.spacer.x2};
`;

const DataValue = styled(Text)`
  margin: 0;
  min-width: 10%;
`;

const LoaderWrapper = styled.div`
  height: ${CONTENT_HEIGHT}px;
  display: flex;
  justify-content: center;
`;

const ContentInfo = styled.div`
  height: ${CONTENT_HEIGHT}px;
  display: flex;
  align-items: center;
  gap: ${tokens.spacer.x1};
  padding-top: ${tokens.spacer.x2};
`;

const ContentEmpty = styled(ContentInfo)`
  justify-content: space-between;
`;

const LineWrapper = styled.div`
  width: 50%;
`;

const StyledIcon = styled(Icon)`
  margin-right: ${tokens.spacer.x0pt5};
`;

const Spacer = styled.div`
  flex: 1;
`;

interface MiniWidgetProps {
  value: string;
  selectedFiltersInUiFormat: GlobalFilter;
  currentSite: TrackableSite;
  selectedDeviceType: HeatmapDeviceTypes;
  selectedTargetingValue: TargetingValue;
  searchPattern: string;
  lineChartData?: { date: Date; value: number | null }[];
  isLoading?: boolean;
  isError?: boolean;
  isEmpty?: boolean;
}

export const MiniWidget = ({
  value,
  lineChartData,
  currentSite,
  selectedDeviceType,
  selectedTargetingValue,
  searchPattern,
  isLoading = false,
  isError = false,
  isEmpty = false,
  selectedFiltersInUiFormat,
}: MiniWidgetProps): JSX.Element => {
  const { t } = useTranslation('heatmaps');
  const siteId = currentSite.id;

  const generateNavigationLink = useCallback(
    (view: string) => {
      return generateNavigationLinkFromHeatmaps({
        view,
        siteId,
        selectedDeviceType,
        selectedTargetingValue,
        selectedFiltersInUiFormat,
        searchPattern,
        applyErrorFilter: true,
      });
    },
    [selectedFiltersInUiFormat, searchPattern, selectedDeviceType, selectedTargetingValue, siteId],
  );

  const trackLink = (elementName: string, cb?: () => void) => {
    trackClick({
      name: elementName,
      uiPosition: EVENT_UI_POSITIONS.SMART_PANEL,
      ...getTrackGroupDataAndTriggeredOnState(currentSite),
    }).finally(() => cb && cb());
  };

  const trendsDescription = t('smart-panel.session-errors.widget.view-trends');
  const trendsIconButtonsDefaultProps = {
    kind: 'ghost' as ButtonKind,
    icon: 'trends' as IconName,
    tooltipText: trendsDescription,
    description: trendsDescription,
  };

  const recordingsIconButtonsDefaultProps = {
    kind: 'ghost' as ButtonKind,
    icon: 'play' as IconName,
    tooltipText: t('smart-panel.session-errors.widget.view-recordings'),
    description: t('smart-panel.session-errors.widget.view-recordings'),
  };

  if (isLoading) {
    return (
      <LoaderWrapper>
        <Loader />
      </LoaderWrapper>
    );
  }

  if (isError) {
    return (
      <ContentInfo>
        <StyledIcon icon="error" />
        <Text>{t('smart-panel.session-errors.widget.error')}</Text>
      </ContentInfo>
    );
  }

  if (isEmpty) {
    return (
      <ContentEmpty>
        <Text textType="subtitle1">{t('smart-panel.session-errors.widget.empty')}</Text>
        <div>
          <IconButton isDisabled {...recordingsIconButtonsDefaultProps} />
          <IconButton isDisabled {...trendsIconButtonsDefaultProps} />
        </div>
      </ContentEmpty>
    );
  }

  return (
    <Content>
      <DataValue textType="subtitle1">{value}</DataValue>
      <LineWrapper>
        {lineChartData && <Line data={lineChartData} stroke={tokens.BRAND_COLORS.ember_700} />}
      </LineWrapper>
      <Spacer aria-hidden="true" />
      <IconButton
        as="a"
        href={generateNavigationLink('playbacks/list')}
        onClick={() => trackLink(EVENT_NAMES.ERROR_WIDGET_VIEW_RECORDINGS_LIST)}
        {...recordingsIconButtonsDefaultProps}
      />
      <IconButton
        as="a"
        href={generateNavigationLink('trends')}
        onClick={() => trackLink(EVENT_NAMES.ERROR_WIDGET_VIEW_TRENDS)}
        {...trendsIconButtonsDefaultProps}
      />
    </Content>
  );
};
